import {
  FORM_FIELD_CELL_SX,
  FORM_FIELD_CELL_SX_MOBILE,
  FORM_FIELD_FULL_ROW_SX,
} from './form-fields-grid-sx'

/** Campi mostrati nel pannello identificazione (sezione crash). */
export const CRASH_IDENTIFICATION_LABELS = new Set([
  'Report date',
  'Crash id',
  'Crash date',
])

/** Tipografia uniforme etichette campi form crash. */
export const FORM_FIELD_LABEL_FONT_SIZE = '0.8125rem'

export const FORM_FIELD_LABEL_LINE_HEIGHT = 1.35

/**
 * @param {{ type?: string, label?: string, options?: Array<{ value?: string | number }>, fullRow?: boolean }} elem
 */
export function isCrashIdentificationField (elem) {
  if (!elem) return false
  return elem.type === 'coordinates' || CRASH_IDENTIFICATION_LABELS.has(elem.label)
}

/**
 * Campi crash esclusa la sezione identificazione.
 * @param {Array<{ type?: string, label?: string }>} elements
 */
export function getCrashDetailElements (elements = []) {
  return elements.filter((elem) => !isCrashIdentificationField(elem))
}

/**
 * @param {Array<{ type?: string }>} elements
 */
export function hasRenderableCrashDetailFields (elements = []) {
  return getCrashDetailElements(elements).some((elem) => elem.type !== 'divider')
}

/**
 * Rimuove divider di sezione senza campi sottostanti.
 * @param {Array<{ type?: string, label?: string, position?: number }>} elements
 */
export function filterOrphanSectionDividers (elements = []) {
  /** @type {typeof elements} */
  const result = []
  /** @type {typeof elements[number] | null} */
  let pendingDivider = null

  for (const elem of elements) {
    if (elem?.type === 'divider') {
      pendingDivider = elem
      continue
    }
    if (pendingDivider) {
      result.push(pendingDivider)
      pendingDivider = null
    }
    result.push(elem)
  }

  return result
}

/**
 * Desktop: due colonne fisse; full row solo per titoli sezione o flag esplicito in config.
 * @param {boolean} isMobile
 * @param {{ type?: string, fullRow?: boolean }} elem
 */
export function shouldFormFieldSpanFullRow (isMobile, elem) {
  if (isMobile || !elem) return false
  if (elem.fullRow === true) return true
  if (elem.type === 'divider') return true
  return false
}

/**
 * @param {boolean} isMobile
 * @param {{ type?: string, label?: string, options?: Array<unknown>, fullRow?: boolean }} elem
 * @returns {import('@mui/material').SxProps}
 */
export function getFormFieldCellSx (isMobile, elem) {
  const base = isMobile ? { ...FORM_FIELD_CELL_SX_MOBILE } : { ...FORM_FIELD_CELL_SX }
  const withLabel = { ...base, ...getFormFieldControlLabelSx(isMobile) }
  if (shouldFormFieldSpanFullRow(isMobile, elem)) {
    return { ...withLabel, ...FORM_FIELD_FULL_ROW_SX }
  }
  return withLabel
}

/** Contenitore campi identificazione crash su mobile (colonna singola). */
export function getIdentificationPanelMobileSx () {
  return {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    ...getFormFieldControlLabelSx(true),
    '& > .MuiFormControl-root': {
      ...FORM_FIELD_CELL_SX_MOBILE,
    },
  }
}

/**
 * @param {boolean} isMobile
 */
export function getFormFieldLabelProps (isMobile) {
  return {
    shrink: true,
    sx: {
      ...getFormFieldLabelOverflowSx(isMobile),
      lineHeight: FORM_FIELD_LABEL_LINE_HEIGHT,
      fontSize: FORM_FIELD_LABEL_FONT_SIZE,
    },
  }
}

/** Evita che MUI tronchi l'icona info accanto all'etichetta (griglia 2 colonne). */
export function getFormFieldLabelOverflowSx (isMobile) {
  return {
    overflow: 'visible',
    maxWidth: 'none',
    textOverflow: 'clip',
    whiteSpace: isMobile ? 'nowrap' : 'normal',
    pointerEvents: 'auto',
    '&.MuiInputLabel-shrink': {
      overflow: 'visible',
      maxWidth: 'none',
      textOverflow: 'clip',
    },
  }
}

/**
 * @param {boolean} [isMobile]
 */
export function getFormFieldLabelSx (isMobile = false) {
  return {
    fontSize: FORM_FIELD_LABEL_FONT_SIZE,
    lineHeight: FORM_FIELD_LABEL_LINE_HEIGHT,
    ...getFormFieldLabelOverflowSx(isMobile),
  }
}

/** Stili FormControl per etichette con tooltip visibile. */
export function getFormFieldControlLabelSx (isMobile = false) {
  return {
    overflow: 'visible',
    '& .MuiInputLabel-root': getFormFieldLabelSx(isMobile),
    '& .MuiFormLabel-root': getFormFieldLabelOverflowSx(isMobile),
  }
}

/**
 * slotProps per DatePicker / DateTimePicker MUI X (label allineata ai TextField).
 * @param {boolean} isMobile
 */
export function getFormPickerSlotProps (isMobile) {
  return {
    textField: {
      margin: 'none',
      InputLabelProps: getFormFieldLabelProps(isMobile),
    },
  }
}

/**
 * @param {import('@mui/material').Theme} theme
 * @param {boolean} isDark
 */
export function getIdentificationPanelSx (theme, isDark) {
  return {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 1.5,
    rowGap: 0.5,
    px: 1,
    pt: 1.5,
    pb: 1,
    mt: 0.25,
    mb: 0.25,
    borderRadius: 1,
    border: '1px solid',
    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(245,245,245,0.65)',
    ...getFormFieldControlLabelSx(false),
    '& > .MuiFormControl-root': {
      ...FORM_FIELD_CELL_SX,
      overflow: 'visible',
    },
  }
}
